/**
 * TranscriptPanel — the transcript viewer itself, fetch-free.
 *
 * Stats strip up top (the numbers that say whether the run is healthy),
 * then the transcript rendered ONCE as flat collapsible rows with search,
 * a tool filter, and expand/collapse-all. Both doors into a run render
 * this same panel so they can't drift apart (TRA-939 follow-up):
 * ConversationTranscript feeds it turns assembled from a conversation
 * listing; TraceKeyTranscript feeds it the server-assembled trajectory of
 * one trace key.
 */

import { useMemo, useState } from 'react'
import { Search } from 'lucide-react'

import {
  TranscriptEvents,
  type DisclosureMode,
} from '@/components/review/TrajectoryTranscript'
import {
  filterEvents,
  flagAnchorIndex,
  toolNames,
  type TranscriptTurn,
} from '@/components/review/transcript'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { RunListItem } from '@/api/generated/model'

/** Sentinel for "no tool filter" — Radix Select forbids an empty-string value. */
const ANY_TOOL = '__any__'

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/** Compact token counts — 98K prompt tokens a turn should read at a glance. */
function formatTokens(n: number | null): string {
  if (n === null) return '—'
  return n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n.toString()
}

function formatElapsed(fromIso: string, toIso: string): string {
  // Floored at 0: the capped-run span mixes write-time clocks whose skew
  // must never render a negative duration.
  const seconds = Math.max(
    0,
    Math.round((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 1000),
  )
  if (seconds < 90) return `${seconds.toString()}s`
  return `${Math.round(seconds / 60).toString()}m`
}

interface StatsStripProps {
  run: RunListItem | undefined
  /** Header cell: what identifies this run ("Conversation" id, "Trace" id). */
  idLabel: string
  idValue: string
  /** The RENDERED turns (possibly capped by the caller, newest kept). */
  turns: TranscriptTurn[]
  /** Whole-run turn count from the complete listing — not the rendered cap. */
  totalTurns: number
  /**
   * Whole-run first request timestamp, ONLY when earlier turns were capped
   * out of `turns`. Null when every turn is rendered.
   */
  cappedFirstSeenIso: string | null
}

/** The at-a-glance numbers that say whether this run is healthy.

    Time/Elapsed/Turns describe the WHOLE run (from the complete listing),
    never just the rendered window — a strip that silently described the
    last 100 turns of a 300-turn run would understate it with no signal
    (REVIEW.md: truncation is never silent). Output tok is the one cell
    that only covers rendered turns, and says so when capped. */
function StatsStrip({
  run,
  idLabel,
  idValue,
  turns,
  totalTurns,
  cappedFirstSeenIso,
}: StatsStripProps): React.JSX.Element {
  const first = turns[0]
  const last = turns[turns.length - 1]
  if (first === undefined || last === undefined) {
    throw new Error('StatsStrip rendered with no turns — caller bug')
  }
  const capped = totalTurns > turns.length
  const startIso = capped && cappedFirstSeenIso !== null ? cappedFirstSeenIso : first.timestamp
  const totalCompletion = turns.reduce((a, t) => a + (t.completionTokens ?? 0), 0)
  // One Time cell: same-day runs read "Aug 8 · 05:28–05:34"; a run crossing
  // midnight names both days.
  const sameDay = formatDay(startIso) === formatDay(last.timestamp)
  const time = sameDay
    ? `${formatDay(startIso)} · ${formatTime(startIso)}–${formatTime(last.timestamp)}`
    : `${formatDay(startIso)} ${formatTime(startIso)} – ${formatDay(last.timestamp)} ${formatTime(last.timestamp)}`
  // A 4-column grid, spans chosen so every row fills exactly: the wide
  // cells (id, time, model) take two columns and the numbers take one.
  // Ragged flex-wrap left half-empty trailing cells and misaligned rows.
  const stats: { label: string; value: string; span?: string; copy?: boolean }[] = [
    { label: 'Time', value: time, span: 'col-span-2' },
    { label: 'Elapsed', value: formatElapsed(startIso, last.timestamp) },
    { label: 'Turns', value: totalTurns.toString() },
    // The final request's history plus its reply = the whole conversation.
    { label: 'Messages', value: (last.messageCount + 1).toString() },
    { label: 'Prompt tok', value: formatTokens(last.promptTokens) },
    {
      label: capped ? `Output tok (last ${turns.length.toString()})` : 'Output tok',
      value: formatTokens(totalCompletion),
      span: 'md:col-span-2',
    },
    { label: 'Model', value: run === undefined ? last.model ?? '—' : run.models.join(', '), span: 'md:col-span-2' },
    { label: 'Tenant', value: run?.client_tenant_name ?? '—' },
    { label: 'Environment', value: run?.environment ?? '—' },
    { label: 'Traffic', value: run?.traffic_type ?? '—' },
    { label: 'Initiated by', value: run?.initiated_by ?? '—' },
    { label: 'Tenant id', value: run?.client_tenant_id ?? '—', span: 'col-span-2', copy: true },
    { label: 'LangSmith run', value: run?.langsmith_run_id ?? '—', span: 'col-span-2', copy: true },
    { label: 'Session', value: run?.session_id ?? '—', span: 'col-span-2', copy: true },
    { label: 'Tags', value: run === undefined || run.tags.length === 0 ? '—' : run.tags.join(', ') },
    { label: 'Flag', value: run === undefined ? '—' : run.flag === null ? 'None' : run.flag.review_status === 'reviewed' ? (run.flag.approved ? 'Good' : 'Bad') : run.flag.review_status },
  ]
  return (
    // gap-px over bg-border draws the hairlines, so cells stay aligned
    // without per-cell border bookkeeping.
    <dl className="grid grid-cols-2 gap-px border border-border bg-border md:grid-cols-4">
      <div className="col-span-2 bg-card px-3.5 py-2">
        <dt className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {idLabel}
        </dt>
        <dd className="mt-0.5 flex items-center gap-1.5 font-mono text-sm">
          <span className="truncate">{idValue}</span>
          <CopyButton value={idValue} label={`Copy ${idLabel.toLowerCase()} ID`} />
        </dd>
      </div>
      {stats.map((stat) => (
        <div key={stat.label} className={cn('bg-card px-3.5 py-2', stat.span)}>
          <dt className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {stat.label}
          </dt>
          <dd className="mt-0.5 flex items-start gap-1.5 font-mono text-sm tabular-nums">
            <span className="min-w-0 break-words [overflow-wrap:anywhere]">{stat.value}</span>
            {stat.copy === true && stat.value !== '—' && <CopyButton value={stat.value} label={`Copy ${stat.label.toLowerCase()}`} />}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/** Section heading in the artifact's uppercase-label register. */
function SectionLabel({ children }: { children: string }): React.JSX.Element {
  return (
    <h3 className="font-mono text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </h3>
  )
}

interface TranscriptPanelProps {
  run: RunListItem | undefined
  /** Assembled turns, oldest first, at least one. */
  turns: TranscriptTurn[]
  idLabel: string
  idValue: string
  totalTurns: number
  cappedFirstSeenIso: string | null
  /** Caller-specific warnings (window/cap notices) between stats and transcript. */
  banners?: React.ReactNode
  /** Caller-specific controls at the end of the transcript header row. */
  actions?: React.ReactNode
  renderReview: (turn: TranscriptTurn) => React.ReactNode
}

export function TranscriptPanel({
  run,
  turns,
  idLabel,
  idValue,
  totalTurns,
  cappedFirstSeenIso,
  banners,
  actions,
  renderReview,
}: TranscriptPanelProps): React.JSX.Element {
  const events = useMemo(() => turns.flatMap((t) => t.events), [turns])
  const tools = useMemo(() => toolNames(events), [events])
  // Per-request latency + status, rendered on each turn's rows now that
  // there is no separate turns table.
  const turnMeta = useMemo(
    () =>
      new Map(
        turns.map((t) => [
          t.traceId,
          { latencyMs: t.latencyMs, statusCode: t.statusCode },
        ]),
      ),
    [turns],
  )

  const [text, setText] = useState('')
  const [tool, setTool] = useState<string>(ANY_TOOL)
  const visible = useMemo(
    () => filterEvents(events, { text, tool: tool === ANY_TOOL ? null : tool }),
    [events, text, tool],
  )
  const isFiltered = text !== '' || tool !== ANY_TOOL

  // Expand/collapse-all works by remounting rows with a new default — see
  // TranscriptEvents. `version` bumps so pressing the same button twice
  // still resets rows the user opened by hand in between.
  const [disclosure, setDisclosure] = useState<{
    mode: DisclosureMode
    version: number
  }>({ mode: 'collapsed', version: 0 })

  return (
    <div className="space-y-5">
      <StatsStrip
        run={run}
        idLabel={idLabel}
        idValue={idValue}
        turns={turns}
        totalTurns={totalTurns}
        cappedFirstSeenIso={cappedFirstSeenIso}
      />

      {banners}

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>Transcript</SectionLabel>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative w-[220px]">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={text}
                onChange={(e) => {
                  setText(e.target.value)
                }}
                placeholder="Search…"
                aria-label="Search this transcript"
                className="h-8 pl-8 text-xs"
              />
            </div>
            <Select value={tool} onValueChange={setTool}>
              <SelectTrigger className="h-8 w-[160px] text-xs" aria-label="Filter by tool">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_TOOL}>Any tool</SelectItem>
                {tools.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => {
                setDisclosure((d) => ({ mode: 'collapsed', version: d.version + 1 }))
              }}
            >
              Collapse all
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => {
                setDisclosure((d) => ({ mode: 'expanded', version: d.version + 1 }))
              }}
            >
              Expand all
            </Button>
            {actions}
          </div>
        </div>

        {isFiltered && (
          <p className="font-mono text-xs text-muted-foreground">
            {visible.length} / {events.length} events match
          </p>
        )}

        {isFiltered && visible.length === 0 && (
          <p className="text-sm text-muted-foreground">No events match this search.</p>
        )}
        {turns.map((turn) => {
          const turnEvents = visible.filter((event) => event.traceId === turn.traceId)
          return (
            <section
              key={turn.traceId}
              aria-label={`Turn ${String(turn.index)}`}
              hidden={isFiltered && turnEvents.length === 0}
              className="ml-4 space-y-1.5"
            >
              <TranscriptEvents
                events={turnEvents}
                allEvents={events}
                mode={disclosure.mode}
                version={disclosure.version}
                meta={turnMeta}
                unfilteredEvents={turn.events}
                reviewIndex={flagAnchorIndex(turn.events, turn.traceId)}
                renderReview={() => renderReview(turn)}
              />
            </section>
          )
        })}
      </div>
    </div>
  )
}
